import { type Chat, type ClientState, previewOf, type Reminder, type Scheduled } from "@rhema/client";
import {
  BellIcon,
  Button,
  ButtonLink,
  ClockIcon,
  EmptyState,
  PageHeader,
  SegmentedControl,
  Toast,
  TopBar,
} from "@rhema/ui";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { textOf } from "../conversation/MessageList";
import { chatTitle } from "../data/view";
import { useClient, useClientState } from "../session/session";
import { homeSpaceId, spaceHref } from "../spaces/data";
import { TimeSheet } from "./TimeSheet";
import { remindChoices, sendChoices, whenLabel } from "./when";

export function chatHref(chat: Chat | undefined, chatId: string, state?: ClientState): string {
  if (chat?.kind === "channel" && chat.space_id)
    return spaceHref(state ? homeSpaceId(state, chat) : chat.space_id, chat.id);
  return `/chats/${chatId}`;
}

function placeOf(state: ClientState, chatId: string): string {
  const chat = state.chats[chatId];
  if (!chat) return "A chat you left";
  if (chat.kind === "channel") {
    const space = state.spaces.find((s) => s.id === homeSpaceId(state, chat));
    return space ? `#${chat.title} in ${space.name}` : `#${chat.title}`;
  }
  return chatTitle(chat, state.me?.id);
}

type Tab = "reminders" | "scheduled";

/** The Later list: reminders you set on messages, and messages you scheduled. */
export function LaterScreen({ variant }: { variant: "phone" | "desktop" }) {
  const client = useClient();
  const state = useClientState();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "scheduled" ? "scheduled" : "reminders";
  const [toast, setToast] = useState<string | null>(null);
  const [snoozing, setSnoozing] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);

  useEffect(() => {
    void client.loadReminders().catch(() => setToast("Your reminders could not be loaded."));
    void client.loadScheduled().catch(() => setToast("Your scheduled messages could not be loaded."));
  }, [client]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const fail = (e: Error) => setToast(e.message);
  const reminders = state.reminders ?? [];
  const scheduled = state.scheduled ?? [];
  const now = Date.now();

  const reminderRow = (r: Reminder) => {
    const due = new Date(r.remindAt).getTime() <= now;
    return (
      <li key={r.id} className="rounded-card border border-line bg-surface px-4 py-3.5">
        <p className="m-0 flex items-center gap-1.5 text-[13px] font-semibold text-ink-secondary">
          <BellIcon size={14} strokeWidth={2.2} className={due ? "text-scarlet" : "text-blue"} />
          <span className={due ? "text-scarlet" : "text-blue"}>
            {due ? "Due" : "Reminder"} {whenLabel(r.remindAt).toLowerCase()}
          </span>
          <span aria-hidden="true">·</span>
          <span className="truncate">{placeOf(state, r.chatId)}</span>
        </p>
        <p className="m-0 mt-1.5 line-clamp-3 text-[15px] leading-normal">{textOf(r.message)}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <ButtonLink href={chatHref(state.chats[r.chatId], r.chatId, state)} size="sm" variant="outline">
            Open the chat
          </ButtonLink>
          <Button size="sm" onClick={() => void client.completeReminder(r.id).catch(fail)}>
            Done
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSnoozing(r.id)}>
            Remind me later
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void client.deleteReminder(r.id).catch(fail)}>
            Remove
          </Button>
        </div>
      </li>
    );
  };

  const scheduledRow = (s: Scheduled) => (
    <li key={s.id} className="rounded-card border border-line bg-surface px-4 py-3.5">
      <p className="m-0 flex items-center gap-1.5 text-[13px] font-semibold text-ink-secondary">
        <ClockIcon size={14} strokeWidth={2.2} className="text-blue" />
        <span className="text-blue">Sends {whenLabel(s.sendAt).toLowerCase()}</span>
        <span aria-hidden="true">·</span>
        <span className="truncate">{placeOf(state, s.chatId)}</span>
      </p>
      <p className="m-0 mt-1.5 line-clamp-3 text-[15px] leading-normal">
        {s.content ? previewOf(s.content) : "Scheduled from another device"}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void client.sendScheduledNow(s.id).catch(fail)}>
          Send now
        </Button>
        <Button size="sm" variant="outline" onClick={() => setMoving(s.id)}>
          Change time
        </Button>
        <Button size="sm" variant="danger" onClick={() => void client.cancelScheduled(s.id).catch(fail)}>
          Delete
        </Button>
      </div>
    </li>
  );

  const list =
    tab === "reminders" ? (
      reminders.length ? (
        <ul aria-label="Reminders" className="m-0 flex list-none flex-col gap-2.5 p-0">
          {reminders.map(reminderRow)}
        </ul>
      ) : (
        <EmptyState icon={<BellIcon size={26} />} title="No reminders" className="py-12">
          Hold a message, or open its menu, and choose Remind me. It shows here until you mark it done.
        </EmptyState>
      )
    ) : scheduled.length ? (
      <ul aria-label="Scheduled messages" className="m-0 flex list-none flex-col gap-2.5 p-0">
        {scheduled.map(scheduledRow)}
      </ul>
    ) : (
      <EmptyState icon={<ClockIcon size={26} />} title="Nothing scheduled" className="py-12">
        Type a message and press the clock beside Send to pick when it goes.
      </EmptyState>
    );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-linen">
      {variant === "phone" ? (
        <TopBar back={{ href: "/you", label: "Back to settings" }} title="Later" />
      ) : (
        <PageHeader title="Later" size="md" />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 min-[900px]:px-6">
        <div className="mx-auto flex max-w-[680px] flex-col gap-4 pt-2">
          <SegmentedControl
            label="Show"
            value={tab}
            onChange={(next) => {
              const p = new URLSearchParams(params);
              if (next === "reminders") p.delete("tab");
              else p.set("tab", next);
              setParams(p, { replace: true });
            }}
            options={[
              { id: "reminders", label: reminders.length ? `Reminders (${reminders.length})` : "Reminders" },
              { id: "scheduled", label: scheduled.length ? `Scheduled (${scheduled.length})` : "Scheduled" },
            ]}
          />
          {list}
        </div>
      </div>
      <TimeSheet
        open={snoozing !== null}
        title="Remind me later"
        choices={remindChoices()}
        pickLabel="Set the reminder"
        onPick={(at) => {
          const id = snoozing;
          setSnoozing(null);
          if (id) void client.snoozeReminder(id, at).catch(fail);
        }}
        onClose={() => setSnoozing(null)}
      />
      <TimeSheet
        open={moving !== null}
        title="Change the time"
        choices={sendChoices()}
        pickLabel="Save the time"
        onPick={(at) => {
          const id = moving;
          setMoving(null);
          if (id) void client.reschedule(id, at).catch(fail);
        }}
        onClose={() => setMoving(null)}
      />
      {toast && (
        <div className="pointer-events-none absolute right-0 bottom-10 left-0 z-50 flex justify-center px-4">
          <Toast>{toast}</Toast>
        </div>
      )}
    </div>
  );
}
